import React, { useState, useEffect, useRef } from 'react';
import { api } from '../services/api';
import { ChevronDown, ChevronUp, Lock, Unlock, Download, CheckCircle, AlertCircle, Circle, Save, ThumbsUp, ThumbsDown, Network,
Search, Loader2, FileCheck, FileX, FileWarning } from 'lucide-react';
import { Toast } from '../components/ui/Toast';
import { LongToast, shouldUseLongToast } from './ui/LongToast';
import useAppreciationsConfig from '../hooks/useAppreciationsConfig';
import { BAREME_PRIMAIRE, motifNoteHorsBareme, noteEnFrancais, versEchelleTranches } from '../utils/baremeNotes';

// Le primaire se note de 0 à 10, au demi-point (décision de Daniel, 7/10/2026).
// Le champ en tient compte, l'enregistrement refuse une note hors barème en la
// nommant, et le serveur refait le même contrôle (scolarite.routes.js,
// `refusNotesHorsBareme`). Les mentions de l'école (`config_appreciations`)
// sont réglées sur 20 : une note y est ramenée (× 2) avant d'être comparée.
const BAREME = BAREME_PRIMAIRE;
const SUR_BAREME = `/${BAREME.max}`;
const CONSIGNE_BAREME = `Au primaire, une note va de 0 à ${noteEnFrancais(BAREME.max)}, au demi-point (7,5 et non 15).`;

// Fonction pour tronquer sans arrondir
const tronquerDecimales = (nombre, decimales = 2) => {
  const multiplicateur = Math.pow(10, decimales);
  return Math.floor(nombre * multiplicateur) / multiplicateur;
};


const TableauNotesPrimaire = ({ eleves, matieres, moisId, moisInfo, annee_scolaire_id, classeId, onSave }) => {

  const [savingEleve, setSavingEleve] = useState(null);
  const [notesData, setNotesData] = useState({});
  const [appreciationsData, setAppreciationsData] = useState({});
  const [notesEnrichies, setNotesEnrichies] = useState([]);
  const [classeInfo, setClasseInfo] = useState(null); 
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [openEleves, setOpenEleves] = useState({});
  const [modificationEnabled, setModificationEnabled] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

const [statsClasse, setStatsClasse] = useState({
  moyenne_plus_elevee: '-',
  moyenne_plus_basse: '-',
  eleve_moyenne_plus_elevee: null,
  eleve_moyenne_plus_basse: null
});
const [statsAnnuelles, setStatsAnnuelles] = useState({
  par_eleve: {},
  stats_classe: null,
  afficher_bilan_annuel: false
});
const [showElevesDossiersIncomplets, setShowElevesDossiersIncomplets] = useState(false);
const [matieresDynamiques, setMatieresDynamiques] = useState([]);  // ⬅️ NOUVEAU
const [eleveSearchTerm, setEleveSearchTerm] = useState('');
const { getAppreciation } = useAppreciationsConfig();

const [configChamps, setConfigChamps] = useState({
  observation_matiere: true,
  appreciation_responsable: true,
  appreciation_directeur: true,
  observation_parents: true
});

// La période AFFICHÉE (classe, mois, année). Le composant reste monté quand on
// change de mois : une réponse qui arrive APRÈS ce changement — le rechargement
// qui suit un enregistrement, par exemple — ne doit jamais remplir la grille de
// Novembre avec les notes d'Octobre. Un clic sur « Enregistrer » les écrirait
// alors comme notes de Novembre, sur une ligne qui leur est désormais propre.
const clePeriode = `${classeId}|${moisId}|${annee_scolaire_id}`;
const periodeAffichee = useRef(clePeriode);
useEffect(() => {
  periodeAffichee.current = clePeriode;
}, [clePeriode]);


  // Charger les notes existantes au montage
useEffect(() => {
  // Même raison que `periodeAffichee` : si l'on change de mois pendant le
  // chargement, la réponse du mois quitté est ignorée.
  let perime = false;
  const chargerNotesPrimaire = async () => {
    try {
      console.log('🔄 Début chargement notes primaire...', { classeId, moisId, annee_scolaire_id });
      
      if (!classeId || !moisId || !annee_scolaire_id) {
        console.warn('⚠️ Paramètres manquants:', { classeId, moisId, annee_scolaire_id });
        setLoading(false);
        return;
      }

      setLoading(true);
      
      // On vide la grille AVANT de charger le nouveau mois. Le composant n'est
      // pas remonté quand on change de mois : sans ce vidage, les notes
      // d'Octobre restaient affichées pendant le chargement de Novembre — et
      // pour de bon si ce chargement échouait. Un clic sur « Enregistrer »
      // les aurait alors écrites comme notes de Novembre.
      setNotesData({});
      setAppreciationsData({});
      setNotesEnrichies([]);

      const result = await api.scolarite.getNotesPrimaire(
        classeId,
        moisId,
        annee_scolaire_id
      );
      if (perime) return;

      console.log('📊 Résultat getNotesPrimaire:', result);
      console.log('📊 Premier élément des données:', result.data?.[0]);
      console.log('🔍 Première note avec verrouille:', result.data?.[0]?.verrouille);
      
      if (result.success) {
        console.log('✅ Succès! Nombre de notes:', result.data?.length || 0);
        setNotesEnrichies(result.data || []);
        setClasseInfo(result.classe_info || null); 
        
        // ⬅️ AJOUTER : Stocker les stats de classe
        if (result.stats_classe) {
        setStatsClasse(result.stats_classe);
        console.log('📊 Stats classe chargées:', result.stats_classe);
        }

        setStatsAnnuelles(result.annuel || { par_eleve: {}, stats_classe: null, afficher_bilan_annuel: false });
        setShowElevesDossiersIncomplets(false);


        // ⬅️ NOUVEAU : Stocker les matières avec coefficients du backend
        if (result.matieres && result.matieres.length > 0) {
          setMatieresDynamiques(result.matieres);
          console.log('📚 Matières avec coefficients chargées:', result.matieres);
        }

        const prefilledData = {};
        const prefilledAppreciations = {};
        
        if (result.data && result.data.length > 0) {
          result.data.forEach(note => {
            if (!prefilledData[note.eleve_id]) {
              prefilledData[note.eleve_id] = {};
            }
            


              let noteValue = '';
                try {
                  if (note.notes) {
                    const notesArray = typeof note.notes === 'string' 
                      ? JSON.parse(note.notes) 
                      : note.notes;
                    
                    // ✅ CORRECTION : Accepter le 0 comme valeur valide
                    const premierNote = notesArray[0];
                    if (premierNote !== null && premierNote !== undefined) {
                      noteValue = premierNote; // Même si c'est 0
                    } else {
                      noteValue = '';
                    }
                  }
                } catch (e) {
                  noteValue = '';
                }

                prefilledData[note.eleve_id][note.matiere_id] = {
                  note: noteValue.toString(), // "0" au lieu de ""
                  appreciation: note.appreciation || ''
                };

            
            if (note.appreciation_mensuelle && !prefilledAppreciations[note.eleve_id]) {
              prefilledAppreciations[note.eleve_id] = {
                appreciation_maitre: note.appreciation_mensuelle.appreciation_maitre || '',
                appreciation_directeur: note.appreciation_mensuelle.appreciation_directeur || '',
                observation_parents: note.appreciation_mensuelle.observation_parents || ''
              };
            }
          });
        }
        
        console.log('📝 Notes pré-remplies:', prefilledData);
        console.log('💬 Appréciations pré-remplies:', prefilledAppreciations);
        
        setNotesData(prefilledData);
        setAppreciationsData(prefilledAppreciations);
        
      } else {
        console.error('❌ Échec:', result.error);
        // Une grille vide qui ne dit rien passerait pour un mois sans notes.
        setToast({
          show: true,
          message: `Les notes de ce mois n'ont pas pu être chargées : ${result.error || result.message || 'erreur inconnue'}. Ne saisissez rien avant de les avoir rechargées.`,
          type: 'error'
        });
      }
    } catch (error) {
      if (perime) return;
      console.error('❌ Exception chargement notes primaire:', error);
      setToast({
        show: true,
        message: `Les notes de ce mois n'ont pas pu être chargées : ${error.message}. Ne saisissez rien avant de les avoir rechargées.`,
        type: 'error'
      });
    } finally {
      // Le chargement du nouveau mois tient son propre indicateur.
      if (!perime) {
        console.log('🏁 Fin chargement, setLoading(false)');
        setLoading(false);
      }
    }
  };

  chargerNotesPrimaire();
  return () => { perime = true; };
}, [classeId, moisId, annee_scolaire_id]);


useEffect(() => {
  const chargerConfigChamps = async () => {
    try {
      const result = await api.notesVerrouillage.getConfigChamps();
      if (result.success) {
        const primaire = result.data.find(r => r.section === 'primaire');
        if (primaire) {
          setConfigChamps({
            observation_matiere: primaire.observation_matiere === 1,
            appreciation_responsable: primaire.appreciation_responsable === 1,
            appreciation_directeur: primaire.appreciation_directeur === 1,
            observation_parents: primaire.observation_parents === 1,
          });
        }
      }
    } catch (err) {
      console.error('❌ Erreur chargement config champs:', err);
    }
  };
  chargerConfigChamps();
}, []);



/**
 * Récupère les données de l'appréciation mensuelle
 * - Si notes enregistrées : utilise la BDD
 * - Si notes NON enregistrées : calcule en temps réel
 */
const getAppreciationMensuelle = (eleveId) => {
  // Chercher dans notesEnrichies (données BDD)
  const noteEleve = notesEnrichies.find(n => n.eleve_id === eleveId);
  
  // ✅ Si l'élève a des notes ENREGISTRÉES (verrouillées), utiliser la BDD
  if (noteEleve && noteEleve.appreciation_mensuelle && noteEleve.verrouille === 1) {
    return {
      moyenne: parseFloat(noteEleve.appreciation_mensuelle.moyenne_mensuelle || 0).toFixed(2),
      total: parseFloat(noteEleve.appreciation_mensuelle.total_notes || 0).toFixed(2),
      classement_position: noteEleve.appreciation_mensuelle.classement_position || 0,
      classement_total: noteEleve.appreciation_mensuelle.classement_total || 0,
      fromDatabase: true
    };
  }
  
  // ✅ Sinon, CALCULER en temps réel depuis notesData
  const matieresAUtiliser = matieresDynamiques.length > 0 ? matieresDynamiques : matieres;
  
  let totalPoints = 0;
  let totalCoefficients = 0;
  let totalNotesSimple = 0;
  let countNotes = 0;

 matieresAUtiliser.forEach(matiere => {
  const note = notesData[eleveId]?.[matiere.id]?.note;
  
  // ✅ CORRECTION ICI
  if (note !== '' && note !== null && note !== undefined) {
    const coefficient = parseFloat(matiere.coefficient || 1);
    const noteValue = parseFloat(note);
    
    totalPoints += noteValue * coefficient;
    totalCoefficients += coefficient;
    totalNotesSimple += noteValue;
    countNotes++;
  }
});

  // Calculer moyenne avec troncature
  let moyenne = 0;
  if (totalCoefficients > 0) {
    const moyenneBrute = totalPoints / totalCoefficients;
    moyenne = Math.floor(moyenneBrute * 100) / 100;
  }
  
  // Calculer classement en temps réel
    const moyennesEleves = eleves.map(e => {
    let pts = 0;
    let coefs = 0;
    matieresAUtiliser.forEach(m => {
      const n = notesData[e.id]?.[m.id]?.note;
      // ✅ CORRECTION ICI
      if (n !== '' && n !== null && n !== undefined) {
        pts += parseFloat(n) * parseFloat(m.coefficient || 1);
        coefs += parseFloat(m.coefficient || 1);
      }
    });
    return {
      id: e.id,
      moyenne: coefs > 0 ? Math.floor((pts / coefs) * 100) / 100 : 0
    };
  }).filter(e => e.moyenne > 0);

  moyennesEleves.sort((a, b) => b.moyenne - a.moyenne);
  const position = moyennesEleves.findIndex(e => e.id === eleveId) + 1;

  return {
    moyenne: moyenne.toFixed(2),
    total: totalNotesSimple.toFixed(2),
    classement_position: position || 0,
    classement_total: moyennesEleves.length,
    fromDatabase: false
  };
};



  const getInfosEnrichies = (eleveId, matiereId) => {
    const noteEnrichie = notesEnrichies.find(
      n => n.eleve_id === eleveId && n.matiere_id === matiereId
    );
    return noteEnrichie || {};
  };

  const handleNoteChange = (eleveId, matiereId, value) => {
    setNotesData(prev => {
      const newData = { ...prev };
      if (!newData[eleveId]) newData[eleveId] = {};
      if (!newData[eleveId][matiereId]) {
        newData[eleveId][matiereId] = { note: '', appreciation: '' };
      }
      newData[eleveId][matiereId].note = value;
      return newData;
    });
  };

  const handleAppreciationMatiereChange = (eleveId, matiereId, value) => {
    setNotesData(prev => {
      const newData = { ...prev };
      if (!newData[eleveId]) newData[eleveId] = {};
      if (!newData[eleveId][matiereId]) {
        newData[eleveId][matiereId] = { note: '', appreciation: '' };
      }
      newData[eleveId][matiereId].appreciation = value;
      return newData;
    });
  };

  const handleAppreciationMensuelleChange = (eleveId, field, value) => {
    setAppreciationsData(prev => ({
      ...prev,
      [eleveId]: {
        ...prev[eleveId],
        [field]: value
      }
    }));
  };

  const toggleEleve = (eleveId) => {
    setOpenEleves(prev => ({
      ...prev,
      [eleveId]: !prev[eleveId]
    }));
  };


  const getStatutEleve = (eleveId) => {
  if (!matieres) return 'empty';
  let notesCount = 0;
  let notesVerrouilleesCount = 0;
  
  matieres.forEach(matiere => {
    const note = notesData[eleveId]?.[matiere.id]?.note;
    const infos = getInfosEnrichies(eleveId, matiere.id);
    
    if (note && note !== '') {
      notesCount++;
      if (infos.verrouille === 1) {
        notesVerrouilleesCount++;
      }
    }
  });

  if (notesCount === 0) return 'empty';
  if (notesCount < matieres.length) return 'partial';
  
  // Si toutes les notes sont verrouillées
  if (notesVerrouilleesCount === matieres.length) return 'complete-locked';
  
  return 'complete';
};


  const StatutBadge = ({ statut }) => {
  const config = {
    complete: {
      icon: CheckCircle,
      text: 'Complet',
      bgColor: 'bg-green-100',
      textColor: 'text-green-700',
      borderColor: 'border-green-300'
    },
    'complete-locked': {
      icon: Lock,
      text: 'Complet et Verrouillé',
      bgColor: 'bg-blue-100',
      textColor: 'text-blue-700',
      borderColor: 'border-blue-300'
    },
    partial: {
      icon: AlertCircle,
      text: 'En cours',
      bgColor: 'bg-orange-100',
      textColor: 'text-orange-700',
      borderColor: 'border-orange-300'
    },
    empty: {
      icon: Circle,
      text: 'Vide',
      bgColor: 'bg-gray-100',
      textColor: 'text-gray-600',
      borderColor: 'border-gray-300'
    }
  };
    const { icon: Icon, text, bgColor, textColor, borderColor } = config[statut];

    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold border ${bgColor} ${textColor} ${borderColor}`}>
        <Icon size={14} />
        {text}
      </span>
    );
  };

  

/**
 * Les notes saisies hors barème (au-dessus de 10, négatives, hors du
 * demi-point, illisibles), écrites « élève - matière : valeur motif ».
 * `eleveIds` restreint la recherche (enregistrement d'un seul élève).
 */
const notesHorsBareme = (eleveIds = null) => {
  const matieresConnues = matieresDynamiques.length > 0 ? matieresDynamiques : matieres;
  const fautives = [];
  Object.entries(notesData).forEach(([eleveId, matiereNotes]) => {
    if (eleveIds && !eleveIds.includes(Number(eleveId))) return;
    Object.entries(matiereNotes || {}).forEach(([matiereId, data]) => {
      const motif = motifNoteHorsBareme(data?.note, BAREME);
      if (!motif) return;
      const eleve = eleves.find(e => Number(e.id) === Number(eleveId));
      const matiere = matieresConnues.find(m => Number(m.id) === Number(matiereId));
      fautives.push(
        `${eleve ? `${eleve.nom} ${eleve.prenom}` : `Élève ${eleveId}`} - ${matiere?.nom || `Matière ${matiereId}`} : ${data.note} ${motif}`
      );
    });
  });
  return fautives;
};

const messageHorsBareme = (fautives) => {
  const citees = fautives.slice(0, 5).join(' ; ');
  const reste = fautives.length > 5 ? ` (et ${fautives.length - 5} autre${fautives.length - 5 > 1 ? 's' : ''})` : '';
  return `${CONSIGNE_BAREME} Corrigez avant d'enregistrer : ${citees}${reste}. Rien n'a été enregistré.`;
};

const handleSave = async () => {
  try {
    if (!annee_scolaire_id) {
      setToast({ show: true, message: '❌ Année scolaire non définie', type: 'error' });
      return;
    }

    // Barème du primaire : rien ne part tant qu'une note en sort.
    const fautives = notesHorsBareme();
    if (fautives.length > 0) {
      setToast({ show: true, message: messageHorsBareme(fautives), type: 'error' });
      return;
    }
    
    setSaving(true);  

    // ========================================
    // 1. PRÉPARER LES NOTES À ENREGISTRER
    // ========================================
   const formattedNotes = [];
    Object.entries(notesData).forEach(([eleveId, matiereNotes]) => {
      Object.entries(matiereNotes).forEach(([matiereId, data]) => {
        // ✅ CORRECTION ICI
        if (data.note !== '' && data.note !== null && data.note !== undefined) {
          formattedNotes.push({
            eleve_id: parseInt(eleveId),
            matiere_id: parseInt(matiereId),
            note: parseFloat(data.note),
            appreciation: data.appreciation || '',
            mois_id: moisId,
            annee_scolaire_id: annee_scolaire_id
          });
        }
      });
    });

    // ========================================
    // 2. PRÉPARER LES APPRÉCIATIONS À ENREGISTRER
    // ========================================
    // ⬅️ SIMPLIFICATION : Ne plus envoyer les calculs, le backend s'en charge
      const formattedAppreciations = eleves.map(eleve => {
        return {
          eleve_id: eleve.id,
          mois_id: moisId,
          annee_scolaire_id: annee_scolaire_id,
          // ⬅️ Le backend calculera : moyenne, total, classements, stats classe
          appreciation_maitre: appreciationsData[eleve.id]?.appreciation_maitre || '',
          appreciation_directeur: appreciationsData[eleve.id]?.appreciation_directeur || '',
          observation_parents: appreciationsData[eleve.id]?.observation_parents || ''
        };
      });
          // ========================================
    // 3. ENVOYER AU BACKEND
    // ========================================
    const response = await api.scolarite.saveNotesPrimaire(
      formattedNotes, 
      formattedAppreciations
    );
    
    // ========================================
    // 4. TRAITER LA RÉPONSE ET RECHARGER
    // ========================================
    if (response.success) {
      onSave && onSave();
      
      // ✅ CORRECTION : Attendre que le backend finisse les calculs
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // ✅ Recharger les données COMPLÈTES avec stats recalculées
      const result = await api.scolarite.getNotesPrimaire(classeId, moisId, annee_scolaire_id);

      console.log('🔄 Rechargement après save:', result);
      console.log('📊 Nouvelles stats classe:', result.stats_classe);

      // Si l'on a changé de mois entre-temps, ce rechargement est celui du mois
      // quitté : il ne remplit pas la grille affichée (voir `periodeAffichee`).
      if (result.success && result.data && periodeAffichee.current === clePeriode) {
        // ========================================
        // 4A. METTRE À JOUR LES NOTES ENRICHIES
        // ========================================
        setNotesEnrichies(result.data);
        
        // ========================================
        // 4B. METTRE À JOUR LES STATS CLASSE (IMPORTANT !)
        // ========================================
        if (result.stats_classe) {
          setStatsClasse(result.stats_classe);
          console.log('✅ Stats classe mises à jour:', result.stats_classe);
        }
        setStatsAnnuelles(result.annuel || { par_eleve: {}, stats_classe: null, afficher_bilan_annuel: false });
        setShowElevesDossiersIncomplets(false);
        
        // ========================================
        // 4C. METTRE À JOUR LES MATIÈRES DYNAMIQUES
        // ========================================
        if (result.matieres && result.matieres.length > 0) {
          setMatieresDynamiques(result.matieres);
          console.log('📚 Matières avec coefficients rechargées:', result.matieres);
        }
        
        // ========================================
        // 4D. RECHARGER LES NOTES DATA ET APPRÉCIATIONS
        // ========================================
        const prefilledData = {};
        const prefilledAppreciations = {};
        
        result.data.forEach(note => {
          if (!prefilledData[note.eleve_id]) {
            prefilledData[note.eleve_id] = {};
          }
          
          let noteValue = '';
          try {
            if (note.notes) {
              const notesArray = typeof note.notes === 'string' 
                ? JSON.parse(note.notes) 
                : note.notes;
              // Un 0 est une note : `notesArray[0] || ''` l'effaçait de la
              // grille au rechargement qui suit l'enregistrement (comme au
              // premier chargement, seule une case vide reste vide).
              const premiere = notesArray[0];
              noteValue = premiere !== null && premiere !== undefined ? premiere : '';
            }
          } catch (e) {
            noteValue = '';
          }
          
          prefilledData[note.eleve_id][note.matiere_id] = {
            note: noteValue.toString(),
            appreciation: note.appreciation || ''
          };
          
          if (note.appreciation_mensuelle && !prefilledAppreciations[note.eleve_id]) {
            prefilledAppreciations[note.eleve_id] = {
              appreciation_maitre: note.appreciation_mensuelle.appreciation_maitre || '',
              appreciation_directeur: note.appreciation_mensuelle.appreciation_directeur || '',
              observation_parents: note.appreciation_mensuelle.observation_parents || ''
            };
          }
        });
        
        setNotesData(prefilledData);
        setAppreciationsData(prefilledAppreciations);
        
        console.log('✅ Toutes les données mises à jour');
        console.log('📊 Stats finales affichées:', {
          moyenneHaute: result.stats_classe?.moyenne_plus_elevee,
          moyenneBasse: result.stats_classe?.moyenne_plus_basse,
          eleveHaut: result.stats_classe?.eleve_moyenne_plus_elevee,
          eleveBas: result.stats_classe?.eleve_moyenne_plus_basse
        });
      }
      
      setToast({ 
        show: true, 
        message: '✅ Notes enregistrées et stats recalculées avec succès !', 
        type: 'success' 
      });
      
    } else {
      throw new Error(response.error || 'Erreur lors de l\'enregistrement');
    }
    
  } catch (error) {
    console.error('❌ Erreur sauvegarde:', error);
    setToast({ 
      show: true, 
      message: `❌ ${error.message}`, 
      type: 'error' 
    });
  } finally {
    setSaving(false); 
  }
};




//Sauvegarde individuelle d'un élève

// 💾 Sauvegarde individuelle d'un élève
const handleSaveEleve = async (eleveId) => {
  try {
    if (!annee_scolaire_id || !moisId) {
      setToast({ show: true, message: '❌ Paramètres manquants', type: 'error' });
      return;
    }

    const eleve = eleves.find(e => e.id === eleveId);
    const prenomNom = `${eleve?.prenom || ''} ${eleve?.nom || ''}`.trim();

    // Barème du primaire : même contrôle que l'enregistrement de la classe.
    const fautives = notesHorsBareme([Number(eleveId)]);
    if (fautives.length > 0) {
      setToast({ show: true, message: messageHorsBareme(fautives), type: 'error' });
      return;
    }

    setSavingEleve(eleveId);
    setToast({ show: true, message: `Enregistrement des notes de ${prenomNom}...`, type: 'loading' });
    
    // Laisser le toast loading s'afficher
    await new Promise(resolve => setTimeout(resolve, 100));

    // Préparer les notes de CET élève uniquement
    const notesEleve = [];
    const matieresAUtiliser = matieresDynamiques.length > 0 ? matieresDynamiques : matieres;

    matieresAUtiliser.forEach(matiere => {
      const data = notesData[eleveId]?.[matiere.id];
      if (data && data.note !== '' && data.note !== null && data.note !== undefined) {
        notesEleve.push({
          matiere_id: matiere.id,
          note: parseFloat(data.note),
          appreciation: data.appreciation || ''
        });
      }
    });

    if (notesEleve.length === 0) {
      setToast({ show: true, message: `Aucune note à enregistrer pour ${prenomNom}`, type: 'error' });
      setSavingEleve(null);
      return;
    }

    const result = await api.scolarite.saveNotesPrimaireEleve({
      eleve_id: eleveId,
      mois_id: moisId,
      annee_scolaire_id: annee_scolaire_id,
      notes: notesEleve,
      appreciation_maitre: appreciationsData[eleveId]?.appreciation_maitre || '',
      appreciation_directeur: appreciationsData[eleveId]?.appreciation_directeur || '',
      observation_parents: appreciationsData[eleveId]?.observation_parents || ''
    });

    if (result.success) {
      // Recharger les données complètes
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const reloadResult = await api.scolarite.getNotesPrimaire(classeId, moisId, annee_scolaire_id);

      // Même garde que l'enregistrement de la classe : jamais la grille d'un
      // autre mois.
      if (reloadResult.success && reloadResult.data && periodeAffichee.current === clePeriode) {
        setNotesEnrichies(reloadResult.data);
        if (reloadResult.stats_classe) setStatsClasse(reloadResult.stats_classe);
        setStatsAnnuelles(reloadResult.annuel || { par_eleve: {}, stats_classe: null, afficher_bilan_annuel: false });
        setShowElevesDossiersIncomplets(false);
        if (reloadResult.matieres?.length > 0) setMatieresDynamiques(reloadResult.matieres);

        // Recharger notesData et appreciationsData
        const prefilledData = {};
        const prefilledAppreciations = {};
        
        reloadResult.data.forEach(note => {
          if (!prefilledData[note.eleve_id]) prefilledData[note.eleve_id] = {};
          
          let noteValue = '';
          try {
            if (note.notes) {
              const notesArray = typeof note.notes === 'string' ? JSON.parse(note.notes) : note.notes;
              const premier = notesArray[0];
              if (premier !== null && premier !== undefined) noteValue = premier;
            }
          } catch (e) { noteValue = ''; }
          
          prefilledData[note.eleve_id][note.matiere_id] = {
            note: noteValue.toString(),
            appreciation: note.appreciation || ''
          };
          
          if (note.appreciation_mensuelle && !prefilledAppreciations[note.eleve_id]) {
            prefilledAppreciations[note.eleve_id] = {
              appreciation_maitre: note.appreciation_mensuelle.appreciation_maitre || '',
              appreciation_directeur: note.appreciation_mensuelle.appreciation_directeur || '',
              observation_parents: note.appreciation_mensuelle.observation_parents || ''
            };
          }
        });
        
        setNotesData(prefilledData);
        setAppreciationsData(prefilledAppreciations);
      }

      setToast({
        show: true,
        message: `Notes de ${prenomNom} enregistrées et stats recalculées !`,
        type: 'success'
      });
    } else {
      throw new Error(result.error || 'Erreur');
    }

  } catch (error) {
    console.error('Erreur save individuel:', error);
    setToast({ show: true, message: `${error.message}`, type: 'error' });
  } finally {
    setSavingEleve(null);
  }
};



   const telechargerBulletin = async (eleveId) => {
  try {
    setToast({ show: true, message: 'Génération du bulletin en cours...', type: 'loading' });
    
    console.log('📄 Demande de génération pour élève:', eleveId);
    
    // Appeler l'API pour générer le PDF
    const pdfBlob = await api.scolarite.genererBulletinPrimaire(
      eleveId,
      moisId,
      annee_scolaire_id,
      classeId
    );
    
    console.log('📄 Blob reçu:', pdfBlob);
    console.log('📄 Type:', pdfBlob.type);
    console.log('📄 Taille:', pdfBlob.size, 'bytes');
    
    // Vérifier que c'est bien un PDF
    if (!pdfBlob || pdfBlob.size === 0) {
      throw new Error('Le fichier reçu est vide');
    }
    

    // ⬅️ AJOUTER ICI : Lire les premiers bytes
    const reader = new FileReader();
    reader.onload = function(e) {
    const arr = new Uint8Array(e.target.result);
    const header = String.fromCharCode.apply(null, arr.slice(0, 100));
    console.log('📄 Début du fichier:', header);
    
    // Vérifier si c'est bien un PDF (commence par %PDF)
    if (!header.startsWith('%PDF')) {
        console.error('❌ Le fichier ne commence pas par %PDF !');
        console.error('❌ Il commence par:', header.substring(0, 50));
    } else {
        console.log('✅ Le fichier est bien un PDF valide');
    }
    };
    reader.readAsArrayBuffer(pdfBlob.slice(0, 100));


    // Trouver le nom de l'élève
    const eleve = eleves.find(e => e.id === eleveId);
    const filename = `bulletin-${eleve.nom}-${eleve.prenom}-${moisInfo.nom}.pdf`;
    
    console.log('📄 Création du lien de téléchargement:', filename);
    
    // Créer un blob explicitement en PDF
    const pdfBlobFixed = new Blob([pdfBlob], { type: 'application/pdf' });
    
    // Créer un lien de téléchargement
    const url = window.URL.createObjectURL(pdfBlobFixed);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    
    // Nettoyer après un délai
    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }, 100);
    
    setToast({ 
      show: true, 
      message: 'Bulletin téléchargé avec succès !', 
      type: 'success' 
    });
    
  } catch (error) {
    console.error('❌ Erreur téléchargement bulletin:', error);
    setToast({ 
      show: true, 
      message: `❌ Erreur: ${error.message}`, 
      type: 'error' 
    });
  }
};


   const telechargerTousBulletins = async () => {
  try {
    const totalEleves = eleves.length;
    const tempsEstimeSecondes = Math.ceil(totalEleves * 2.5); // ~2.5 secondes par bulletin
    const tempsEstimeMinutes = Math.ceil(tempsEstimeSecondes / 60); // Convertir en minutes
    
    // Message adapté selon la durée
    const messageTemps = tempsEstimeMinutes >= 2 
      ? `environ ${tempsEstimeMinutes} minutes`
      : `environ 1 minute`;
    
    // Toast de chargement avec estimation
    setToast({ 
      show: true, 
      message: `Génération de ${totalEleves} bulletins en cours... Patience, ${messageTemps}.`, 
      type: 'loading' 
    });
    
    console.log('📦 Demande de génération de tous les bulletins');
    
    // Appeler l'API pour générer le ZIP
    const zipBlob = await api.scolarite.genererTousBulletinsClasse(
      classeId,
      moisId,
      annee_scolaire_id
    );
    
    console.log('📦 ZIP reçu:', zipBlob.size, 'bytes');
    
    // Créer un nom de fichier
    const classe = classeInfo?.nom || 'classe';
    const moisNom = moisInfo?.nom || 'mois';
    const filename = `bulletins-${classe}-${moisNom}.zip`;
    
    // Créer un lien de téléchargement
    const url = window.URL.createObjectURL(zipBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    
    // Nettoyer après un délai
    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }, 100);
    
    // Toast de succès
    setToast({ 
      show: true, 
      message: ` ${totalEleves} bulletins téléchargés avec succès !`, 
      type: 'success' 
    });
    
  } catch (error) {
    console.error('❌ Erreur téléchargement bulletins:', error);
    setToast({ 
      show: true, 
      message: `❌ Erreur lors de la génération: ${error.message}`, 
      type: 'error' 
    });
  }
};


const afficherBilanAnnuelPrimaire = statsAnnuelles?.afficher_bilan_annuel === true;

const formatNoteAnnuelle = (value) => {
  const note = parseFloat(value);
  return Number.isNaN(note) ? '-' : note.toFixed(2);
};

const getBilanAnnuelEleve = (eleveId) => {
  return statsAnnuelles?.par_eleve?.[eleveId] || statsAnnuelles?.par_eleve?.[String(eleveId)] || null;
};

const getElevesBilanAnnuelIncomplet = () => {
  return Object.values(statsAnnuelles?.par_eleve || {})
    .filter((bilan) => bilan && !bilan.complet)
    .sort((a, b) => `${a.nom} ${a.prenom}`.localeCompare(`${b.nom} ${b.prenom}`, 'fr'));
};

const formatRangAnnuel = (bilan) => {
  const position = bilan?.classement_annuel?.position;
  const total = bilan?.classement_annuel?.total;
  if (!position || !total) return '-';
  return `${position}${position === 1 ? 'er' : 'ème'} / ${total}`;
};

const formatMoisManquantsBilan = (bilan) => {
  const moisManquants = Array.isArray(bilan?.mois_manquants) ? bilan.mois_manquants : [];
  if (moisManquants.length > 0) {
    return moisManquants.map((mois) => mois.nom).join(', ');
  }

  const trimestres = Array.isArray(bilan?.trimestres_incomplets) ? bilan.trimestres_incomplets : [];
  return trimestres.map((trimestre) => `T${trimestre}`).join(', ') || '-';
};

const AutoAppreciationBadge = ({ note }) => {
  if (note === '' || note === null || note === undefined) return null;
  const noteValue = parseFloat(note);
  if (isNaN(noteValue)) return null;
  // Une note hors barème n'a pas de mention : le champ la signale déjà.
  if (motifNoteHorsBareme(note, BAREME)) return null;
  // Tranches de l'école réglées sur 20 : 7,5/10 se lit 15/20.
  const app = getAppreciation(versEchelleTranches(noteValue, BAREME), 'note');
  if (!app || app.libelle === '-') return null;
  return (
    <span className={`inline-block text-[10px] px-2 py-0.5 rounded-full font-semibold whitespace-nowrap ${app.couleur}`}>
      {app.libelle}
    </span>
  );
};




  return (
    <div className="w-full space-y-6 relative">
      {saving && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
            <div className="bg-white rounded-2xl shadow-2xl p-8 flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-[#246990]"></div>
            <p className="text-lg font-semibold text-gray-700">Enregistrement en cours...</p>
            </div>
        </div>
        )}

      {/* Un message long (le refus « deux mois dans le même trimestre », par
          exemple) ne doit pas s'effacer en trois secondes : LongToast reste
          jusqu'à fermeture. Les messages courts gardent le Toast habituel. */}
      {toast.show && (
        shouldUseLongToast(toast.message, toast.type) ? (
          <LongToast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast({ ...toast, show: false })}
          />
        ) : (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast({ ...toast, show: false })}
          />
        )
      )}

        <div className="bg-gradient-to-r from-[#246990] to-[#3084b5] rounded-xl p-6 shadow-lg">
        <div className="flex items-center justify-between">
            <div>
            <h2 className="text-xl font-bold text-white flex items-center gap-3">
               Bulletin Primaire - {moisInfo ? `${moisInfo.nom} (Trimestre ${moisInfo.trimestre})` : `Mois ${moisId}`}
            </h2>
            <p className="text-blue-100 mt-1">{eleves.length} élèves • {matieres.length} matières • notes sur {BAREME.max}, au demi-point</p>
            </div>
            
            <div className="flex items-center gap-3">
            {/* Bouton Modification avec tooltip élégant */}
            <div className="relative group">
                <button
                onClick={() => setModificationEnabled(!modificationEnabled)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                    modificationEnabled
                    ? 'bg-[#319190]/90 text-white hover:bg-[#319190]'
                    : 'bg-white/20 text-white hover:bg-white/30'
                }`}
                >
                {modificationEnabled ? <Unlock size={18} /> : <Lock size={18} />}
                {modificationEnabled ? 'Modification activée' : 'Modification verrouillée'}
                </button>
                
                {/* Tooltip élégant */}
                <div className="absolute left-0 top-full mt-2 w-96 bg-white text-gray-800 text-xs rounded-lg shadow-xl p-3 hidden group-hover:block z-50 border border-blue-200">
                <div className="flex items-start gap-2">
                    <Lock className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                    <div>
                    <p className="font-semibold text-blue-700 mb-1">Protection des modifications</p>
                    <p className="text-gray-600">
                        Ce bouton permet d'activer ou désactiver la modification des notes <span className="font-semibold">non encore enregistrées</span>. 
                        Les notes déjà verrouillées (en rouge) <span className="font-semibold text-red-600">ne peuvent jamais être modifiées</span> sans autorisation de l'administrateur.
                    </p>
                    </div>
                </div>
                {/* Petite flèche */}
                <div className="absolute -top-2 left-8 w-4 h-4 bg-white border-l border-t border-blue-200 transform rotate-45"></div>
                </div>
            </div>

            <button
                onClick={telechargerTousBulletins}
                className="flex items-center gap-2 px-4 py-2 bg-white text-[#246990] rounded-lg font-medium hover:bg-blue-50 transition-all"
            >
                <Download size={18} />
                Tous les bulletins
            </button>
            </div>
        </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
        <div className="bg-gradient-to-br from-[#319190]/10 to-[#319190]/30 rounded-xl p-4 border border-[#319190]/40">
            <div className="flex items-center gap-2 text-sm font-medium text-[#319190]/90">
            <div className="bg-[#319190]/90 p-2 rounded-full">
                <ThumbsUp size={16} className="text-white" />
            </div>
            Moyenne la plus élevée
            </div>
            <div className="text-3xl font-bold text-[#319190]/90 mt-1">
            {statsClasse.moyenne_plus_elevee || '-'}
            </div>
            {statsClasse.eleve_moyenne_plus_elevee && (
            <div className="mt-3 inline-block">
                <div className="bg-white px-3 py-2 rounded-lg shadow-md border border-[#319190]/20">
                <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-[#319190] rounded-full"></div>
                    <span className="text-sm font-bold text-gray-800">
                    {statsClasse.eleve_moyenne_plus_elevee}
                    </span>
                </div>
                </div>
            </div>
            )}
        </div>

        <div className="bg-gradient-to-br from-[#ae7467]/10 to-[#ae7467]/30 rounded-xl p-4 border border-[#ae7467]/40">
            <div className="flex items-center gap-2 text-sm font-medium text-[#ae7467]/90">
            <div className="bg-[#ae7467]/90 p-2 rounded-full">
                <ThumbsDown size={16} className="text-white" />
            </div>
            Moyenne la plus basse
            </div>
            <div className="text-3xl font-bold text-[#ae7467]/90 mt-1">
            {statsClasse.moyenne_plus_basse || '-'}
            </div>
            {statsClasse.eleve_moyenne_plus_basse && (
            <div className="mt-3 inline-block">
                <div className="bg-white px-3 py-2 rounded-lg shadow-md border border-[#ae7467]/20">
                <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-[#ae7467] rounded-full"></div>
                    <span className="text-sm font-bold text-gray-800">
                    {statsClasse.eleve_moyenne_plus_basse}
                    </span>
                </div>
                </div>
            </div>
            )}
        </div>
        </div>

      {afficherBilanAnnuelPrimaire && (() => {
        const stats = statsAnnuelles?.stats_classe || {};
        const hasAnnualStats = Number(stats.total_eleves_bilan_complet || 0) > 0;
        const elevesBilanIncomplet = getElevesBilanAnnuelIncomplet();

        return (
          <div className="bg-white rounded-xl shadow-sm border border-[#246990]/15 p-4">
            <div className="flex items-center justify-between gap-3 mb-4">
              <div>
                <h3 className="text-sm font-bold text-[#246990] uppercase tracking-wide">Bilan annuel de la classe</h3>
                <p className="text-xs text-gray-500 mt-1">Calculé depuis les moyennes mensuelles, regroupées en trimestres.</p>
              </div>
              <span className="text-xs font-semibold text-[#319190] bg-[#319190]/10 px-3 py-1 rounded-full">
                {stats.total_eleves_bilan_complet || 0} bilan{Number(stats.total_eleves_bilan_complet || 0) > 1 ? 's' : ''} complet{Number(stats.total_eleves_bilan_complet || 0) > 1 ? 's' : ''}
              </span>
            </div>

            {hasAnnualStats ? (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div className="rounded-lg border border-[#319190]/20 bg-[#319190]/5 p-3">
                  <p className="text-xs font-medium text-gray-600">Meilleure moyenne annuelle</p>
                  <p className="text-2xl font-bold text-[#319190] mt-1">{formatNoteAnnuelle(stats.moyenne_plus_elevee)}<span className="text-sm text-gray-500">{SUR_BAREME}</span></p>
                  <p className="text-xs font-semibold text-gray-700 mt-2 truncate">{stats.eleve_moyenne_plus_elevee || '-'}</p>
                </div>
                <div className="rounded-lg border border-[#ae7467]/20 bg-[#ae7467]/5 p-3">
                  <p className="text-xs font-medium text-gray-600">Moyenne annuelle la plus basse</p>
                  <p className="text-2xl font-bold text-[#ae7467] mt-1">{formatNoteAnnuelle(stats.moyenne_plus_basse)}<span className="text-sm text-gray-500">{SUR_BAREME}</span></p>
                  <p className="text-xs font-semibold text-gray-700 mt-2 truncate">{stats.eleve_moyenne_plus_basse || '-'}</p>
                </div>
                <div className="rounded-lg border border-[#246990]/20 bg-[#246990]/5 p-3">
                  <p className="text-xs font-medium text-gray-600">Moyenne annuelle de la classe</p>
                  <p className="text-2xl font-bold text-[#246990] mt-1">{formatNoteAnnuelle(stats.moyenne_classe_annuelle)}<span className="text-sm text-gray-500">{SUR_BAREME}</span></p>
                  <p className="text-xs text-gray-500 mt-2">Élèves avec bilan complet</p>
                </div>
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="text-xs font-medium text-gray-600">Dossiers incomplets</p>
                      <p className="text-2xl font-bold text-amber-700 mt-1">{stats.total_eleves_incomplets || 0}</p>
                    </div>
                    {elevesBilanIncomplet.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setShowElevesDossiersIncomplets((prev) => !prev)}
                        className="h-8 w-8 rounded-full border border-amber-200 bg-white text-amber-700 hover:bg-amber-100 flex items-center justify-center transition-colors"
                        aria-label={showElevesDossiersIncomplets ? 'Masquer les dossiers incomplets' : 'Voir les dossiers incomplets'}
                      >
                        {showElevesDossiersIncomplets ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </button>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">Mois de l'année manquants</p>
                </div>
              </div>
            ) : (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                <div>Données annuelles incomplètes : aucun élève n'a encore toutes les moyennes mensuelles attendues.</div>
                {elevesBilanIncomplet.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setShowElevesDossiersIncomplets((prev) => !prev)}
                    className="mt-3 inline-flex items-center gap-2 rounded-full border border-amber-200 bg-white px-3 py-1.5 text-xs font-semibold text-amber-700 hover:bg-amber-100 transition-colors"
                  >
                    Voir les {elevesBilanIncomplet.length} élève{elevesBilanIncomplet.length > 1 ? 's' : ''} concerné{elevesBilanIncomplet.length > 1 ? 's' : ''}
                    {showElevesDossiersIncomplets ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  </button>
                )}
              </div>
            )}

            {elevesBilanIncomplet.length > 0 && showElevesDossiersIncomplets && (
              <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50/70 p-3">
                <div className="flex items-center justify-between gap-3 mb-3">
                  <p className="text-sm font-semibold text-amber-800">Élèves avec dossier annuel incomplet</p>
                  <span className="text-xs font-bold text-amber-700 bg-white border border-amber-200 rounded-full px-2 py-0.5">
                    {elevesBilanIncomplet.length}
                  </span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-48 overflow-y-auto pr-1">
                  {elevesBilanIncomplet.map((bilan) => (
                    <div key={bilan.eleve_id} className="rounded-md border border-amber-100 bg-white px-3 py-2">
                      <p className="text-sm font-semibold text-gray-800 truncate">{bilan.nom} {bilan.prenom}</p>
                      <p className="text-xs text-amber-700 mt-1">Manquant : {formatMoisManquantsBilan(bilan)}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {notesEnrichies.some(n => n.verrouille === 1) && (
        <div className="bg-amber-50 border-l-4 border-amber-400 rounded-lg p-4 flex items-start gap-3">
          <Lock size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-amber-800">
              Certaines notes sont verrouillées
            </p>
            <p className="text-xs text-amber-700 mt-1">
              Les notes déjà enregistrées sont automatiquement verrouillées. Pour toute modification, veuillez contacter votre administrateur.
            </p>
          </div>
        </div>
      )}

      {!eleves || !matieres || eleves.length === 0 || matieres.length === 0 ? (
        <div className="p-8 text-center text-gray-500 bg-gray-50 rounded-xl">
          <p>Aucun élève ou matière disponible pour cette classe.</p>
        </div>
      ) : (

        <div className="space-y-3">
        {/* Recherche d'élève */}
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          <input
            type="text"
            placeholder="Rechercher un élève par nom..."
            value={eleveSearchTerm}
            onChange={(e) => setEleveSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/20 outline-none transition-all"
          />
        </div>


        
        {/* ============ BANDEAU COMPLÉTUDE ============ */}
        {(() => {
          const matieresAUtiliser = matieresDynamiques.length > 0 ? matieresDynamiques : matieres;
          const totalMatieres = matieresAUtiliser.length;
          
          let complets = 0;
          let incomplets = 0;
          let vides = 0;
          const incompletsList = [];
          const videsList = [];

          eleves.forEach(eleve => {
            let count = 0;
            const manquantes = [];
            
            matieresAUtiliser.forEach(matiere => {
              const note = notesData[eleve.id]?.[matiere.id]?.note;
              if (note !== '' && note !== null && note !== undefined) {
                count++;
              } else {
                manquantes.push(matiere.nom);
              }
            });

            if (count === 0) {
              vides++;
              videsList.push({ nom: `${eleve.nom} ${eleve.prenom}`, manquantes });
            } else if (count < totalMatieres) {
              incomplets++;
              incompletsList.push({ nom: `${eleve.nom} ${eleve.prenom}`, manquantes, count, total: totalMatieres });
            } else {
              complets++;
            }
          });

          if (eleves.length === 0 || totalMatieres === 0) return null;

          return (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-4">
              <div className="flex items-center gap-2 mb-3">
                <FileCheck size={18} className="text-[#246990]" />
                <span className="text-sm font-bold text-gray-700">Complétude des bulletins</span>
                <span className="text-xs text-gray-400">({totalMatieres} matières attendues)</span>
              </div>
              
              {/* Barre de progression */}
              <div className="flex rounded-full overflow-hidden h-3 mb-3">
                {complets > 0 && (
                  <div className="bg-green-500 transition-all" style={{ width: `${(complets / eleves.length) * 100}%` }} 
                    title={`${complets} complet${complets > 1 ? 's' : ''}`} />
                )}
                {incomplets > 0 && (
                  <div className="bg-orange-400 transition-all" style={{ width: `${(incomplets / eleves.length) * 100}%` }}
                    title={`${incomplets} incomplet${incomplets > 1 ? 's' : ''}`} />
                )}
                {vides > 0 && (
                  <div className="bg-gray-300 transition-all" style={{ width: `${(vides / eleves.length) * 100}%` }}
                    title={`${vides} vide${vides > 1 ? 's' : ''}`} />
                )}
              </div>

              {/* Compteurs */}
              <div className="flex flex-wrap gap-4 text-xs">
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  <span className="text-gray-600">Complets :</span>
                  <span className="font-bold text-green-700">{complets}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-orange-400"></div>
                  <span className="text-gray-600">Incomplets :</span>
                  <span className="font-bold text-orange-700">{incomplets}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-gray-300"></div>
                  <span className="text-gray-600">Vides :</span>
                  <span className="font-bold text-gray-600">{vides}</span>
                </div>
              </div>

              {/* Détails incomplets */}
              {incomplets > 0 && (
                <details className="mt-3 border-t border-gray-100 pt-3">
                  <summary className="text-xs font-semibold text-orange-600 cursor-pointer flex items-center gap-1">
                    <FileWarning size={14} />
                    {incomplets} bulletin{incomplets > 1 ? 's' : ''} incomplet{incomplets > 1 ? 's' : ''}, cliquez pour voir
                  </summary>
                  <div className="mt-2 space-y-2 max-h-40 overflow-y-auto">
                    {incompletsList.map((item, i) => (
                      <div key={i} className="bg-orange-50 rounded-lg p-2 text-xs border border-orange-200">
                        <span className="font-semibold text-gray-700">{item.nom}</span>
                        <span className="text-orange-600 ml-2">({item.count}/{item.total})</span>
                        <div className="text-orange-500 mt-1">
                          Manque : {item.manquantes.join(', ')}
                        </div>
                      </div>
                    ))}
                  </div>
                </details>
              )}

              {/* Détails vides */}
              {vides > 0 && (
                <details className="mt-3 border-t border-gray-100 pt-3">
                  <summary className="text-xs font-semibold text-gray-500 cursor-pointer flex items-center gap-1">
                    <FileX size={14} />
                    {vides} bulletin{vides > 1 ? 's' : ''} vide{vides > 1 ? 's' : ''}, cliquez pour voir
                  </summary>
                  <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                    {videsList.map((item, i) => (
                      <div key={i} className="bg-gray-50 rounded-lg p-2 text-xs border border-gray-200">
                        <span className="font-semibold text-gray-700">{item.nom}</span>
                        <span className="text-gray-400 ml-2">(aucune note)</span>
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          );
        })()}



        {eleves
          .filter(eleve => {
            if (!eleveSearchTerm.trim()) return true;
            const search = eleveSearchTerm.toLowerCase().trim();
            return `${eleve.nom} ${eleve.prenom}`.toLowerCase().includes(search);
          })
          .map((eleve) => {


            const isOpen = openEleves[eleve.id];
            const statut = getStatutEleve(eleve.id);
            
            // ⬅️ MODIFICATION : Récupérer depuis la BDD au lieu de calculer
            const appreciation = getAppreciationMensuelle(eleve.id);
            const moyenne = appreciation.moyenne;
            const classement = {
              position: appreciation.classement_position,
              total: appreciation.classement_total
            };
            const bilanAnnuel = getBilanAnnuelEleve(eleve.id);

            return (
              <div key={eleve.id} className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200">
                <div
                  onClick={() => toggleEleve(eleve.id)}
                  className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-4 flex-1">
                    <button className="text-[#246990] hover:text-[#3084b5] transition-colors">
                      {isOpen ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
                    </button>
                    <div>
                      <h3 className="text-lg font-bold text-[#246990]/90">
                        {eleve.nom} {eleve.prenom}
                      </h3>

                    <div className="flex items-center gap-3 text-sm text-gray-900">
                    <span>Moyenne: {moyenne}</span>
                    <span>•</span>
                    <div className="relative group inline-flex items-center gap-1">
                        <span>
                        Classement: {classement.position === 1 ? '1er' : `${classement.position}ème`} / {classement.total}
                        </span>


                            <AlertCircle className="w-3.5 h-3.5 text-gray-400 cursor-help" />
                            
                            {/* Tooltip Classement */}
                            <div className="absolute left-0 top-full mt-2 w-80 bg-white text-gray-800 text-xs rounded-lg shadow-xl p-3 hidden group-hover:block z-50 border border-blue-200">
                            <div className="flex items-start gap-2">
                                <AlertCircle className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                                <div>
                                <p className="font-semibold text-blue-700 mb-1">Classement général</p>
                                <p className="text-gray-600">
                                    Le classement indique la <span className="font-semibold">position globale de l'élève</span> basée sur sa moyenne générale de toutes les matières. 
                                    Cela permet de voir sa performance d'ensemble par rapport à ses camarades.
                                </p>
                                </div>
                            </div>
                            <div className="absolute -top-2 left-8 w-4 h-4 bg-white border-l border-t border-blue-200 transform rotate-45"></div>
                            </div>
                        </div>
                        </div>

                    </div>
                  </div>

                 
                
                <div className="flex items-center gap-2">
                  <StatutBadge statut={statut} />
                  
                  {/* 💾 Bouton sauvegarde individuelle */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSaveEleve(eleve.id);
                    }}
                    disabled={savingEleve === eleve.id || !modificationEnabled || statut === 'complete-locked'}
                    className={`p-2 rounded-lg transition-all ${
                      statut === 'complete-locked'
                        ? 'text-gray-300 cursor-not-allowed opacity-40'
                        : savingEleve === eleve.id
                        ? 'text-gray-400 cursor-wait'
                        : !modificationEnabled
                        ? 'text-gray-300 cursor-not-allowed'
                        : 'text-[#319190] hover:bg-[#319190]/10'
                    }`}
                    title={
                      statut === 'complete-locked'
                        ? 'Bulletin complet et verrouillé'
                        : !modificationEnabled
                        ? 'Activez la modification pour sauvegarder'
                        : `Enregistrer les notes de ${eleve.prenom}`
                    }
                  >
                    <Save size={20} />
                  </button>

                  {/* 📥 Bouton téléchargement bulletin */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      telechargerBulletin(eleve.id);
                    }}
                    className="p-2 text-[#246990] hover:bg-blue-50 rounded-lg transition-colors"
                    title={`Télécharger le bulletin de ${eleve.prenom}`}
                  >
                    <Download size={20} />
                  </button>
                </div>
              

                </div>

                {isOpen && (
                  <div className="p-4 bg-gray-50 border-t border-gray-200">
                    <div className="space-y-4">
                      <div className="bg-white rounded-lg overflow-hidden shadow-sm">
                        <table className="w-full">


                          <thead className="bg-gradient-to-r from-[#319190] to-[#63b5b4] text-white">
                            <tr>
                              <th className="py-3 px-4 text-left font-medium">Discipline</th>
                              
                              {/* ⬅️ NOUVELLE COLONNE : Coefficient */}
                              <th className="py-3 px-4 text-center font-medium w-20">
                                <div className="flex items-center justify-center gap-1 relative group">
                                  Coef.
                               </div>
                               </th>
                              
                              <th className="py-3 px-4 text-center font-medium w-32">Note</th>
                              
                              <th className="py-3 px-4 text-center font-medium w-24">
                                <div className="flex items-center justify-center gap-1 relative group">
                                  Rang
                                  <AlertCircle className="w-4 h-4 cursor-help" />
                                  
                                  {/* Tooltip Rang */}
                                  <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 w-80 bg-white text-gray-800 text-xs rounded-lg shadow-xl p-3 hidden group-hover:block z-50 border border-yellow-200">
                                    <div className="flex items-start gap-2">
                                      <AlertCircle className="w-4 h-4 text-yellow-500 flex-shrink-0 mt-0.5" />
                                      <div>
                                        <p className="font-semibold text-yellow-700 mb-1">Rang par matière</p>
                                        <p className="text-gray-600">
                                          Le rang indique la <span className="font-semibold">position de l'élève dans cette matière spécifique</span> par rapport à ses camarades. 
                                          Par exemple, "2/4" signifie qu'il est 2ème sur 4 élèves en calcul écrit.
                                        </p>
                                      </div>
                                    </div>
                                    <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-white border-l border-t border-yellow-200 transform rotate-45"></div>
                                  </div>
                                </div>
                              </th>
                              
                              <th className="py-3 px-4 text-left font-medium">Appréciation</th>
                              
                              <th className="py-3 px-4 text-center font-medium w-40">
                                Modifié par
                              </th>
                            </tr>
                          </thead>


                          <tbody>
                            {(matieresDynamiques.length > 0 ? matieresDynamiques : matieres).map((matiere) => {
                              const infosEnrichies = getInfosEnrichies(eleve.id, matiere.id);
                              const isNoteVerrouillee = infosEnrichies.verrouille === 1;
                              const motifNote = motifNoteHorsBareme(notesData[eleve.id]?.[matiere.id]?.note, BAREME);
                              
                              console.log(`Matière ${matiere.id} - Verrouillée:`, isNoteVerrouillee, infosEnrichies);
                              
                              return (
                                <tr key={matiere.id} className="border-b border-gray-100 hover:bg-gray-50"><td className="py-3 px-4 font-medium text-gray-700">
                                    <div className="flex items-center gap-2">
                                      {matiere.nom}
                                      {infosEnrichies.is_transferred === 1 && (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-teal-100 text-teal-700 border border-teal-200" title={`Note transférée depuis ${infosEnrichies.source_site}`}>
                                          <Network size={10} />
                                          Site source
                                        </span>
                                      )}
                                    </div>
                                  </td>

                                  {/* ⬅️ NOUVELLE CELLULE : Coefficient */}
                                  <td className="py-3 px-4 text-center">
                                    <span className="inline-block bg-purple-100 text-purple-800 px-2 py-1 rounded-full text-xs font-bold">
                                      {matiere.coefficient || infosEnrichies.coefficient || 1}
                                    </span>
                                  </td>
  

                                  <td className="py-3 px-4">
                                    <div className="relative">

                                  <input
                                    type="number"
                                    inputMode="decimal"
                                    min="0"
                                    max={BAREME.max}
                                    step={BAREME.pas}
                                    disabled={!modificationEnabled || isNoteVerrouillee}
                                    aria-invalid={Boolean(motifNote)}
                                    className={`w-24 p-2 border rounded-lg text-center ${
                                      isNoteVerrouillee
                                        ? 'bg-red-50 border-red-300 cursor-not-allowed'
                                        : motifNote
                                        ? 'border-red-500 bg-red-50 text-red-700 focus:ring-2 focus:ring-red-400'
                                        : modificationEnabled
                                        ? 'border-[#246990] focus:ring-2 focus:ring-[#246990]'
                                        : 'bg-gray-100 cursor-not-allowed'
                                    }`}
                                    value={notesData[eleve.id]?.[matiere.id]?.note ?? ''}  // ✅ CHANGER ICI
                                    onChange={(e) => handleNoteChange(eleve.id, matiere.id, e.target.value)}
                                    title={isNoteVerrouillee ? "Note verrouillée - Contactez votre administrateur" : `Note sur ${BAREME.max}, au demi-point`}
                                  />

                                      {!isNoteVerrouillee && motifNote && (
                                        <p className="mt-1 text-xs font-medium text-red-600">
                                          Sur {BAREME.max} : la note {motifNote}
                                        </p>
                                      )}

                                      
                                      {isNoteVerrouillee && (
                                        <div className="flex items-center gap-1 mt-1">
                                          <Lock size={10} className="text-red-500" />
                                          <span className="text-[10px] text-red-600 font-medium">Verrouillée</span>
                                        </div>
                                      )}
                                    </div>
                                  </td>


                                  <td className="py-3 px-4 text-center">
                                    <span className="inline-block bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm font-bold">
                                      {infosEnrichies.rang ? `${infosEnrichies.rang}/${infosEnrichies.total_eleves}` : '-'}
                                    </span>
                                  </td>

                                  <td className="py-3 px-4">
                                    <div className="flex items-center gap-2">
                                      <AutoAppreciationBadge note={notesData[eleve.id]?.[matiere.id]?.note} />
                                      {configChamps.observation_matiere && (
                                        <input
                                          type="text"
                                          disabled={!modificationEnabled || isNoteVerrouillee}
                                          className={`flex-1 p-2 border rounded-lg text-sm ${
                                            isNoteVerrouillee
                                              ? 'bg-red-50 border-red-300 cursor-not-allowed'
                                              : modificationEnabled
                                              ? 'border-gray-300 focus:ring-2 focus:ring-[#246990]'
                                              : 'bg-gray-100 cursor-not-allowed'
                                          }`}
                                          value={notesData[eleve.id]?.[matiere.id]?.appreciation || ''}
                                          onChange={(e) => handleAppreciationMatiereChange(eleve.id, matiere.id, e.target.value)}
                                          placeholder={isNoteVerrouillee ? "Verrouillée" : "Observation..."}
                                          title={isNoteVerrouillee ? "Note déjà enregistrée et verrouillée." : ""}
                                        />
                                      )}
                                    </div>
                                  </td>




                                 {/* ⬅️ COLONNE : Modifié par - CENTRÉ */}
                                <td className="py-3 px-4 text-center">
                                {infosEnrichies.enregistre_par ? (
                                    <div className="text-xs inline-block">
                                    <div className="flex items-center justify-center gap-1 text-gray-700">
                                        <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                        </svg>
                                        <span className="font-medium">{infosEnrichies.enregistre_par}</span>
                                    </div>
                                    <div className="text-gray-500 mt-0.5">
                                        {new Date(infosEnrichies.updated_at).toLocaleDateString('fr-FR', {
                                        day: '2-digit',
                                        month: '2-digit',
                                        year: 'numeric'
                                        })}
                                        {' '}
                                        {new Date(infosEnrichies.updated_at).toLocaleTimeString('fr-FR', {
                                        hour: '2-digit',
                                        minute: '2-digit'
                                        })}
                                    </div>
                                    </div>
                                ) : (
                                    <span className="text-xs text-gray-400 italic">Non enregistré</span>
                                )}
                                </td>

                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      <div className="bg-white rounded-lg p-4 shadow-sm">
                      {(configChamps.appreciation_responsable || configChamps.appreciation_directeur || configChamps.observation_parents) && (
                        <h4 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                            Appréciations Mensuelles
                        </h4>
                      )}
                                            
                        {/* ⬅️ NOUVEAU : Vérifier si au moins une note est verrouillée */}
                        {(() => {
                            const hasVerrouillage = matieres.some(matiere => {
                            const infos = getInfosEnrichies(eleve.id, matiere.id);
                            return infos.verrouille === 1;
                            });
                            
                            return (
                            <>
                                {/* ⬅️ NOUVEAU : Alerte si verrouillé */}

                                {hasVerrouillage && (configChamps.appreciation_responsable || configChamps.appreciation_directeur || configChamps.observation_parents) && (
                                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-3 flex items-start gap-2">
                                    <Lock size={16} className="text-red-600 flex-shrink-0 mt-0.5" />
                                    <div className="text-xs text-red-700">
                                    <p className="font-semibold">Appréciations verrouillées</p>
                                    <p className="mt-1">
                                        Les appréciations sont verrouillées car cet élève a au moins une note enregistrée. 
                                        Pour les modifier, contactez votre administrateur.
                                    </p>
                                    </div>
                                </div>
                                )}
                                
                                <div className="grid grid-cols-1 gap-3">
                                  {[
                                  { key: 'appreciation_maitre', label: 'Appréciation du Maître', configKey: 'appreciation_responsable' },
                                  { key: 'appreciation_directeur', label: 'Appréciation du Directeur', configKey: 'appreciation_directeur' },
                                  { key: 'observation_parents', label: 'Observation des Parents', configKey: 'observation_parents' },
                                ].filter(f => configChamps[f.configKey]).map(({ key: field, label }) => (
                                    <div key={field}>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                  {label}
                                </label>
                                    <textarea
                                        disabled={!modificationEnabled || hasVerrouillage}
                                        className={`w-full p-2 border rounded-lg ${
                                        hasVerrouillage
                                            ? 'bg-red-50 border-red-300 cursor-not-allowed'
                                            : modificationEnabled
                                            ? 'border-gray-300 focus:ring-2 focus:ring-[#246990]'
                                            : 'bg-gray-100 cursor-not-allowed'
                                        }`}
                                        rows="2"
                                        value={appreciationsData[eleve.id]?.[field] || ''}
                                        onChange={(e) => handleAppreciationMensuelleChange(eleve.id, field, e.target.value)}
                                        placeholder={
                                        hasVerrouillage 
                                            ? 'Verrouillé' 
                                            : `Saisir ${field === 'appreciation_maitre' ? "l'appréciation" : field === 'appreciation_directeur' ? "l'appréciation" : "l'observation"}...`
                                        }
                                    />
                                    
                                    {/* ⬅️ NOUVEAU : Indicateur de verrouillage */}
                                    {hasVerrouillage && (
                                        <div className="flex items-center gap-1 mt-1">
                                        <Lock size={10} className="text-red-500" />
                                        <span className="text-[10px] text-red-600 font-medium">Verrouillé</span>
                                        </div>
                                    )}
                                    </div>
                                ))}
                                </div>
                            </>
                            );
                        })()}

                        {afficherBilanAnnuelPrimaire && (
                          <div className="bg-[#246990]/5 rounded-lg p-4 shadow-sm border border-[#246990]/15 mt-4">
                            <div className="flex items-center justify-between gap-3 mb-3">
                              <h4 className="font-bold text-[#246990] text-sm uppercase tracking-wide">Parcours annuel</h4>
                              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${bilanAnnuel?.complet ? 'bg-[#319190]/10 text-[#319190]' : 'bg-amber-100 text-amber-700'}`}>
                                {bilanAnnuel?.complet ? 'Bilan complet' : 'Données incomplètes'}
                              </span>
                            </div>

                            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                              <div className="bg-white rounded-lg border border-gray-200 p-3 text-center">
                                <p className="text-xs text-gray-500 font-medium">T1</p>
                                <p className="text-lg font-bold text-gray-800 mt-1">{formatNoteAnnuelle(bilanAnnuel?.moyennes_trimestrielles?.[1])}</p>
                              </div>
                              <div className="bg-white rounded-lg border border-gray-200 p-3 text-center">
                                <p className="text-xs text-gray-500 font-medium">T2</p>
                                <p className="text-lg font-bold text-gray-800 mt-1">{formatNoteAnnuelle(bilanAnnuel?.moyennes_trimestrielles?.[2])}</p>
                              </div>
                              <div className="bg-white rounded-lg border border-gray-200 p-3 text-center">
                                <p className="text-xs text-gray-500 font-medium">T3</p>
                                <p className="text-lg font-bold text-gray-800 mt-1">{formatNoteAnnuelle(bilanAnnuel?.moyennes_trimestrielles?.[3])}</p>
                              </div>
                              <div className="bg-white rounded-lg border border-[#319190]/20 p-3 text-center">
                                <p className="text-xs text-gray-500 font-medium">Moyenne annuelle</p>
                                <p className="text-lg font-bold text-[#319190] mt-1">{bilanAnnuel?.complet ? `${formatNoteAnnuelle(bilanAnnuel.moyenne_annuelle)}${SUR_BAREME}` : '-'}</p>
                              </div>
                              <div className="bg-white rounded-lg border border-[#246990]/20 p-3 text-center">
                                <p className="text-xs text-gray-500 font-medium">Rang annuel</p>
                                <p className="text-lg font-bold text-[#246990] mt-1">{bilanAnnuel?.complet ? formatRangAnnuel(bilanAnnuel) : '-'}</p>
                              </div>
                            </div>

                            {!bilanAnnuel?.complet && (
                              <p className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                                Manquant : {formatMoisManquantsBilan(bilanAnnuel)}
                              </p>
                            )}
                          </div>
                        )}
                        {/* Récapitulatif de l'élève */}
                        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-6 shadow-sm border border-blue-200">
                        <div className="grid grid-cols-2 gap-6">
                            {/* Colonne gauche : Enseignant */}
                            <div>
                            <p className="text-sm font-medium text-gray-600 mb-2">Enseignant principal</p>
                            <p className="text-lg font-bold text-[#246990]">
                                {classeInfo?.enseignant_nom || 'Non assigné'}
                            </p>
                            </div>

                            {/* Colonne droite : Statistiques de l'élève */}
                            <div className="text-right">
                            <p className="text-sm font-medium text-gray-800 mb-2">Performance de l'élève</p>


                            <div className="space-y-1">
                            {/* Moyenne générale avec tooltip */}
                            <div className="flex justify-end items-baseline gap-2 relative group">
                                <span className="text-sm text-gray-800">Moyenne générale :</span>
                                <span className="text-3xl font-bold text-[#319190]">{moyenne}</span>
                                <span className="text-sm text-gray-500">{SUR_BAREME}</span>
                                <AlertCircle className="w-4 h-4 text-gray-400 cursor-help ml-1 mb-2" />
                                

                           {/* Tooltip */}
                              <div className="absolute right-0 bottom-full mb-2 w-80 bg-white text-gray-800 text-xs rounded-lg shadow-xl p-3 hidden group-hover:block z-50 border border-blue-200">
                                <div className="flex items-start gap-2">
                                  <AlertCircle className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                                  <div>
                                    <p className="font-semibold text-blue-700 mb-1">Calcul de la moyenne avec coefficients</p>
                                    <p className="text-gray-600">
                                      La moyenne est calculée en multipliant chaque note par son <span className="font-semibold">coefficient</span>, 
                                      puis en divisant le <span className="font-semibold">total pondéré</span> par la <span className="font-semibold">somme des coefficients</span>.
                                    </p>
                                    <p className="text-gray-500 mt-2 text-xs bg-gray-50 p-2 rounded">
                                      <span className="font-semibold">Exemple :</span><br/>
                                      Note 8 (coef. 2) + Note 6 (coef. 1)<br/>
                                      = (8×2 + 6×1) ÷ (2+1)<br/>
                                      = (16 + 6) ÷ 3<br/>
                                      = <span className="font-bold">7.33</span> sur {BAREME.max}
                                    </p>
                                    <p className="text-gray-600 mt-2">
                                      Moyenne actuelle : <span className="font-bold text-[#319190]">{moyenne}{SUR_BAREME}</span>
                                    </p>
                                  </div>
                                </div>
                                {/* Petite flèche */}
                                <div className="absolute -bottom-2 right-12 w-4 h-4 bg-white border-r border-b border-blue-200 transform rotate-45"></div>
                              </div>

                            </div>
                            
                            {/* Classement */}
                            <div className="flex justify-end items-center gap-2 text-sm mt-2">
                                <span className="text-gray-800">Classement :</span>
                                <span className="font-semibold text-[#246990]">
                                {classement.position === 1 ? '1er' : `${classement.position}ème`} sur {classement.total} élève{classement.total > 1 ? 's' : ''}
                                </span>
                            </div>
                            </div>


                            </div>
                        </div>
                        </div>

                   </div>

                        {/* ⬅️ AJOUTER : Traçabilité */}
                     {(() => {
                        // Récupérer toutes les notes de l'élève avec leurs employés
                        const notesAvecEmployes = matieres
                            .map(m => getInfosEnrichies(eleve.id, m.id))
                            .filter(n => n.enregistre_par);
                        
                        if (notesAvecEmployes.length === 0) {
                            return null;
                        }
                        
                        // Grouper par employé
                        const parEmploye = {};
                        notesAvecEmployes.forEach(note => {
                            if (!parEmploye[note.enregistre_par]) {
                            parEmploye[note.enregistre_par] = {
                                count: 0,
                                lastUpdate: note.updated_at
                            };
                            }
                            parEmploye[note.enregistre_par].count++;
                            if (new Date(note.updated_at) > new Date(parEmploye[note.enregistre_par].lastUpdate)) {
                            parEmploye[note.enregistre_par].lastUpdate = note.updated_at;
                            }
                        });
                        
                        return (
                            <div className="bg-gray-50 rounded-lg p-3 border border-gray-200 text-xs">
                            <div className="font-semibold text-gray-700 mb-2 flex items-center gap-2">
                                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                                </svg>
                                Historique des modifications
                            </div>
                            <div className="space-y-1">
                                {Object.entries(parEmploye).map(([employe, data]) => (
                                <div key={employe} className="flex items-center justify-between text-gray-600">
                                    <span>
                                    <span className="font-medium text-gray-700">{employe}</span>
                                    <span className="text-gray-400"> • {data.count} note{data.count > 1 ? 's' : ''}</span>
                                    </span>
                                    <span className="text-gray-500">
                                    {new Date(data.lastUpdate).toLocaleDateString('fr-FR', {
                                        day: '2-digit',
                                        month: '2-digit',
                                        year: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                    })}
                                    </span>
                                </div>
                                ))}
                            </div>
                            </div>
                        );
                        })()}


                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="flex justify-end">
        <button
        onClick={handleSave}
        disabled={saving || !modificationEnabled}
        className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm shadow-md transition-all ${
            saving || !modificationEnabled
            ? 'bg-gray-300 cursor-not-allowed text-gray-500'
            : 'bg-gradient-to-r from-[#246990] to-[#3084b5] text-white hover:shadow-lg hover:scale-105'
        }`}
        >
        <Save size={16} />
        {saving ? 'Enregistrement...' : 'Enregistrer les notes'}
        </button>
      </div>
    </div>
  );
};

export default TableauNotesPrimaire;